import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { AlertTriangle, CheckCircle2, MoreVertical, Search } from "lucide-react";

/* ============================================================
   SHARED UI COMPONENTS — Bootstrap edition
   Generic, screen-agnostic pieces from the Component Library
   Specification (Starter Pack §6). Built on Bootstrap 5 utility
   and component classes (toast, modal, badge, placeholder,
   spinner, dropdown) plus custom-theme.css for ChatBizz branding.
   Any screen — Auth, Knowledge Base, Conversations, Leads — can
   import these instead of reimplementing its own toast/modal/
   skeleton/empty/error UI.
   ============================================================ */

export function Toast({ toast, onClose }) {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1080 }}>
      <div
        role="status"
        className={`toast show border ${isError ? "border-danger" : "border-success"}`}
        style={{ minWidth: 280 }}
      >
        <div className="d-flex align-items-start">
          <div className="toast-body d-flex align-items-start gap-2 flex-grow-1">
            {isError ? (
              <AlertTriangle size={18} className="text-danger flex-shrink-0 mt-1" />
            ) : (
              <CheckCircle2 size={18} className="text-success flex-shrink-0 mt-1" />
            )}
            <span>{toast.message}</span>
          </div>
          <button
            type="button"
            className="btn-close me-2 mt-2"
            aria-label="Dismiss notification"
            onClick={onClose}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Generic modal shell (Bootstrap backdrop + dialog + content), manually
 * controlled — no Bootstrap JS/Popper dependency. Centralizes the
 * Escape-to-close behavior so individual modals (ConfirmDialog, any
 * screen's create/edit form) don't each reimplement it. Pass the modal's
 * header/body/footer markup as children.
 */
export function Modal({ open, onClose, closeDisabled, labelledBy, role = "dialog", size, scrollable, children }) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !closeDisabled) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closeDisabled, onClose]);

  if (!open) return null;

  const sizeClass = size === "lg" ? "modal-lg" : size === "sm" ? "modal-sm" : "";

  return (
    <>
      <div className="modal-backdrop show" style={{ zIndex: 1050 }} />
      <div className="modal d-block" tabIndex="-1" role={role} aria-modal="true" aria-labelledby={labelledBy} style={{ zIndex: 1055 }}>
        <div
          className={`modal-dialog modal-dialog-centered ${scrollable ? "modal-dialog-scrollable" : ""} ${sizeClass}`.trim()}
        >
          <div className="modal-content">{children}</div>
        </div>
      </div>
    </>
  );
}

/**
 * Generic search input with a leading icon (Component Spec §6, Data group:
 * "SearchBox"). Any list screen — Bot List, Knowledge Base, Conversations,
 * Leads — can use this instead of hand-rolling its own input-group markup.
 */
export function SearchBox({ value, onChange, placeholder = "Search...", ariaLabel }) {
  return (
    <div className="input-group">
      <span className="input-group-text bg-white">
        <Search size={16} className="text-secondary" />
      </span>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        className="form-control"
      />
    </div>
  );
}

export function ConfirmDialog({ open, title, description, confirmLabel, danger, onConfirm, onCancel, busy }) {
  return (
    <Modal open={open} onClose={onCancel} closeDisabled={busy} labelledBy="confirm-title" role="alertdialog">
      <div className="modal-body">
        <h5 id="confirm-title" className="fw-bold mb-2">
          {title}
        </h5>
        <p className="text-secondary mb-0">{description}</p>
      </div>
      <div className="modal-footer border-0 pt-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={`btn ${danger ? "btn-danger" : "btn-primary"} d-inline-flex align-items-center gap-2`}
          onClick={onConfirm}
          disabled={busy}
        >
          {busy && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
          {confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

export function FieldLabel({ children, required, htmlFor }) {
  return (
    <label className="form-label fw-medium" htmlFor={htmlFor}>
      {children} {required && <span className="text-danger">*</span>}
    </label>
  );
}

/**
 * Generic active/inactive status badge (Component Spec §6 "Status: Badge").
 */
export function StatusBadge({ status, activeLabel = "Active", inactiveLabel = "Inactive" }) {
  const isActive = status === "active";
  return (
    <span
      className={`badge rounded-pill d-inline-flex align-items-center gap-1 fw-medium ${
        isActive ? "bg-success-subtle text-success-emphasis" : "bg-danger-subtle text-danger-emphasis"
      }`}
    >
      <span className={`status-dot ${isActive ? "status-dot-active" : "status-dot-inactive"}`} />
      {isActive ? activeLabel : inactiveLabel}
    </span>
  );
}

/**
 * Generic loading-row placeholder for a table body, using Bootstrap's
 * built-in `placeholder` utility (no custom skeleton CSS needed). Column
 * count/shape here matches the Bot List table; adapt <td> count if reused
 * for a screen with a different column layout.
 */
export function RowSkeleton({ columns = 4 }) {
  return (
    <tr>
      <td>
        <div className="d-flex align-items-center gap-2">
          <span className="placeholder rounded-circle" style={{ width: 36, height: 36 }} />
          <div className="flex-grow-1">
            <span className="placeholder col-6 d-block mb-1" />
            <span className="placeholder col-8 d-block" />
          </div>
        </div>
      </td>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="d-none d-md-table-cell">
          <span className="placeholder col-6" />
        </td>
      ))}
      <td>
        <span className="placeholder col-4" />
      </td>
      <td />
    </tr>
  );
}

/**
 * Generic empty state. Screen-specific copy/icon/action are supplied by the
 * caller — this component only handles shared layout/styling.
 */
export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-5 px-3 border border-dashed rounded-3">
      {Icon && (
        <div
          className="bg-primary-subtle text-primary rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
          style={{ width: 48, height: 48 }}
        >
          <Icon size={24} />
        </div>
      )}
      <h3 className="h6 fw-bold mb-1">{title}</h3>
      <p className="text-secondary small mx-auto mb-3" style={{ maxWidth: 280 }}>
        {description}
      </p>
      {action}
    </div>
  );
}

/**
 * Generic error state (API/service failure). Screen-specific copy/action
 * supplied by the caller.
 */
export function ErrorState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-5 px-3 border border-danger-subtle bg-danger-subtle bg-opacity-25 rounded-3">
      {Icon && (
        <div
          className="bg-danger-subtle text-danger rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
          style={{ width: 48, height: 48 }}
        >
          <Icon size={24} />
        </div>
      )}
      <h3 className="h6 fw-bold mb-1">{title}</h3>
      <p className="text-secondary small mx-auto mb-3" style={{ maxWidth: 280 }}>
        {description}
      </p>
      {action}
    </div>
  );
}

/**
 * Generic row action menu (Bootstrap dropdown styling, manually controlled —
 * no Bootstrap JS/Popper dependency). Takes an `items` array of
 * { key, label, icon, danger, onClick }. Pass an empty/undefined array to
 * render nothing — used for role-based UI gating.
 *
 * The dropdown itself is rendered through a React portal into
 * document.body, positioned using the toggle button's actual screen
 * coordinates, rather than being nested inside the table row. This is
 * deliberate: Bootstrap's `.table-responsive` sets `overflow-x: auto`,
 * which per the CSS spec makes `overflow-y` behave as `auto` too — so any
 * dropdown nested inside the table gets clipped by that scroll container.
 * Portaling to the body sidesteps that entirely, regardless of table size
 * or scroll position.
 */
export function ActionMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const openMenu = () => {
    const rect = btnRef.current.getBoundingClientRect();
    const MENU_WIDTH = 176;
    setCoords({
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKeyDown);
    // Close on scroll/resize rather than trying to reposition, since the
    // menu's coordinates were computed at open-time.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (!items || items.length === 0) return null;

  return (
    <div className="d-inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        className="btn btn-sm btn-light border-0"
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
      >
        <MoreVertical size={16} />
      </button>
      {open &&
        ReactDOM.createPortal(
          <ul
            role="menu"
            className="dropdown-menu show"
            style={{ position: "fixed", top: coords.top, left: coords.left, minWidth: 176, zIndex: 1080 }}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  role="menuitem"
                  className={`dropdown-item d-flex align-items-center gap-2 ${item.danger ? "text-danger" : ""}`}
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}